/**
 * Which mime types a picker may offer when the upload target is PUBLIC.
 *
 * This is the client mirror of `PUBLICLY_SERVABLE_MIME`
 * (`modules/file_management/public_asset_mime.py`) — the allowlist
 * `FileService.upload` enforces on every row it writes `access_mode='public'`.
 * `/api/v1/files/{id}/public` is the one endpoint that streams **inline**,
 * unauthenticated, with the **stored** mime and never sniffs, so an
 * `image/svg+xml` stored public is script execution on our own origin. That
 * refusal is correct and permanent; what was missing was a picker that knew
 * about it, so a tenant choosing an SVG avatar got a live preview and then a
 * 400 on an upload that used to work.
 *
 * **This is not `isBrowserRenderableImage`, and must never be folded into it.**
 * That predicate answers "will a browser paint this in an `<img>`?", it is also
 * what chooses between an `<img>` and a download card, and SVG genuinely IS
 * renderable — so listing SVG there would make every already-stored PRIVATE SVG
 * render as a file card. The two questions disagree in both directions: SVG is
 * renderable and not servable, `application/pdf` is servable and not renderable.
 *
 * The Python file stays the source of truth (it is where the security reasoning
 * for each entry lives, and where the writer-side gate runs). Drift between the
 * two lists is caught by `tools/check_public_servable_mime_parity.py`, so
 * widening one without the other fails the build rather than shipping a picker
 * that offers what the backend refuses.
 */

/** Every mime `files.access_mode = 'public'` may hold. Mirrors the backend set. */
export const PUBLICLY_SERVABLE_MIME: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/apng',
  'image/x-icon',
  'image/vnd.microsoft.icon',
  'image/bmp',
  'image/avif',
  'image/heic',
  'image/heif',
  'application/pdf',
];

/**
 * True when `mimeType` may be stored on a public target.
 *
 * Normalises the way the backend's `base_mime` does — a `File.type` and a stored
 * `Content-Type` both carry parameters and arbitrary case
 * (`image/SVG+XML; charset=utf-8`), and a membership test against the raw string
 * would wave exactly the format this list exists to refuse straight through.
 */
export function isPubliclyServableMime(mimeType: string | null | undefined): boolean {
  if (!mimeType) return false;
  const base = mimeType.split(';', 1)[0].trim().toLowerCase();
  return PUBLICLY_SERVABLE_MIME.includes(base);
}

/**
 * The `accept` string for an image picker whose target is public.
 *
 * `application/pdf` is dropped: a public IMAGE picker renders its result in an
 * `<img>` (an avatar, a logo, a product photo), so offering a PDF would trade a
 * 400 for a broken-image glyph. Note `accept` only filters the OS picker — a
 * drag, a paste, or a user switching the dialog to "All files" reaches the
 * upload regardless, so it is the advertisement and never the gate; the callers
 * validate with `isPubliclyServableMime` as well.
 */
export const PUBLICLY_SERVABLE_IMAGE_ACCEPT: string = PUBLICLY_SERVABLE_MIME.filter(
  // not-a-renderer: partitions this allowlist into "image" and "document" so an
  // image picker does not offer the PDF. Every entry here is already known to be
  // paintable, so `isBrowserRenderableImage` would answer the same and mean
  // something else.
  (mime) => mime.startsWith('image/'),
).join(',');
